<script>
  import { onMount } from 'svelte';
  import { getWorkoutsByMonth, getWorkouts, getWorkoutWithExercises } from '../services/db';
  import { syncStatus } from '../stores/app.js';

  let currentYear = new Date().getFullYear();
  let currentMonth = new Date().getMonth() + 1; // 1-12
  let workoutDates = {}; // { day: count }
  let previousSyncState = false;

  // Dialog state
  let showDialog = false;
  let selectedDate = null;
  let selectedWorkouts = [];

  const monthNames = ['1月', '2月', '3月', '4月', '5月', '6月', '7月', '8月', '9月', '10月', '11月', '12月'];
  const weekDays = ['日', '一', '二', '三', '四', '五', '六'];

  onMount(async () => {
    await loadMonth(currentYear, currentMonth);
  });

  async function loadMonth(year, month) {
    workoutDates = await getWorkoutsByMonth(year, month);
  }

  // Watch for sync completion (transition from syncing to not syncing)
  $: {
    const wasSyncing = previousSyncState;
    const isCurrentlySyncing = $syncStatus?.isSyncing || false;

    // Reload data when sync completes successfully (transition from syncing to not syncing)
    if (wasSyncing && !isCurrentlySyncing && $syncStatus?.success) {
      loadMonth(currentYear, currentMonth);
    }

    previousSyncState = isCurrentlySyncing;
  }

  // Also reload when sync status shows success and not syncing (for initial load)
  $: if ($syncStatus?.success && !$syncStatus?.isSyncing && Object.keys(workoutDates).length === 0) {
    loadMonth(currentYear, currentMonth);
  }

  async function previousMonth() {
    if (currentMonth === 1) {
      currentMonth = 12;
      currentYear--;
    } else {
      currentMonth--;
    }
    await loadMonth(currentYear, currentMonth);
  }

  async function nextMonth() {
    const now = new Date();
    const nowYear = now.getFullYear();
    const nowMonth = now.getMonth() + 1;

    // Don't go beyond current month
    if (currentYear === nowYear && currentMonth === nowMonth) {
      return;
    }

    if (currentMonth === 12) {
      currentMonth = 1;
      currentYear++;
    } else {
      currentMonth++;
    }
    await loadMonth(currentYear, currentMonth);
  }

  // Get calendar grid (array of weeks, each week has 7 days)
  function getCalendarDays() {
    const firstDay = new Date(currentYear, currentMonth - 1, 1);
    const lastDay = new Date(currentYear, currentMonth, 0);
    const firstDayOfWeek = firstDay.getDay(); // 0-6 (Sun-Sat)
    const daysInMonth = lastDay.getDate();

    const days = [];
    let week = [];

    // Fill empty days before first day
    for (let i = 0; i < firstDayOfWeek; i++) {
      week.push(null);
    }

    // Fill actual days
    for (let day = 1; day <= daysInMonth; day++) {
      week.push(day);
      if (week.length === 7) {
        days.push(week);
        week = [];
      }
    }

    // Fill remaining empty days
    if (week.length > 0) {
      while (week.length < 7) {
        week.push(null);
      }
      days.push(week);
    }

    return days;
  }

  function hasWorkout(day) {
    return day && workoutDates[day] > 0;
  }

  function isToday(day) {
    if (!day) return false;
    const now = new Date();
    return currentYear === now.getFullYear() &&
           currentMonth === now.getMonth() + 1 &&
           day === now.getDate();
  }

  async function handleDateClick(day) {
    if (!hasWorkout(day)) return;

    const dateStr = new Date(currentYear, currentMonth - 1, day).toISOString();
    const startOfDay = new Date(currentYear, currentMonth - 1, day, 0, 0, 0).toISOString();
    const endOfDay = new Date(currentYear, currentMonth - 1, day, 23, 59, 59).toISOString();

    const workouts = await getWorkouts({
      startDate: startOfDay,
      endDate: endOfDay
    });

    // Load exercises for each workout
    const workoutsWithExercises = await Promise.all(
      workouts.map(async (workout) => {
        const data = await getWorkoutWithExercises(workout.id);
        return data;
      })
    );

    selectedDate = `${currentYear}年${currentMonth}月${day}日`;
    selectedWorkouts = workoutsWithExercises;
    showDialog = true;
  }

  function closeDialog() {
    showDialog = false;
    selectedDate = null;
    selectedWorkouts = [];
  }

  // Format time (HH:MM)
  function formatTime(dateStr) {
    const date = new Date(dateStr);
    return date.toLocaleTimeString('zh-TW', { hour: '2-digit', minute: '2-digit', hour12: false });
  }

  // Calculate total volume for a workout
  function calculateWorkoutVolume(exercises) {
    let total = 0;
    exercises.forEach(exercise => {
      if (Array.isArray(exercise.sets)) {
        exercise.sets.forEach(set => {
          const weight = parseFloat(set.weight) || 0;
          const reps = parseInt(set.reps, 10) || 0;
          total += weight * reps;
        });
      }
    });
    return total;
  }

  $: calendarDays = getCalendarDays();
  $: isCurrentMonth = currentYear === new Date().getFullYear() && currentMonth === new Date().getMonth() + 1;
</script>

<div class="card">
  <!-- Header -->
  <div class="flex justify-between items-center mb-4">
    <h2 class="text-xl font-semibold text-gray-800">訓練日曆</h2>
    <div class="flex items-center gap-2">
      <button
        on:click={previousMonth}
        class="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        title="上個月"
      >
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
        </svg>
      </button>

      <div class="text-lg font-medium min-w-[100px] text-center">
        {currentYear}年 {monthNames[currentMonth - 1]}
      </div>

      <button
        on:click={nextMonth}
        class="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        class:opacity-50={isCurrentMonth}
        class:cursor-not-allowed={isCurrentMonth}
        disabled={isCurrentMonth}
        title="下個月"
      >
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
        </svg>
      </button>
    </div>
  </div>

  <!-- Calendar Grid -->
  <div class="overflow-x-auto">
    <!-- Week day headers -->
    <div class="grid grid-cols-7 gap-1 mb-1">
      {#each weekDays as day}
        <div class="text-center text-xs font-medium text-gray-600 py-1">
          {day}
        </div>
      {/each}
    </div>

    <!-- Calendar days -->
    <div class="space-y-1">
      {#each calendarDays as week}
        <div class="grid grid-cols-7 gap-1">
          {#each week as day}
            {#if day}
              <div class="flex items-center justify-center py-1">
                <button
                  type="button"
                  on:click={() => handleDateClick(day)}
                  class="w-6 h-6 rounded-full flex items-center justify-center text-xs transition-colors"
                  class:bg-primary-600={hasWorkout(day)}
                  class:text-white={hasWorkout(day)}
                  class:text-gray-800={!hasWorkout(day)}
                  class:ring-1={isToday(day) && !hasWorkout(day)}
                  class:ring-primary-600={isToday(day) && !hasWorkout(day)}
                  class:ring-2={isToday(day) && hasWorkout(day)}
                  class:ring-white={isToday(day) && hasWorkout(day)}
                  class:cursor-pointer={hasWorkout(day)}
                  class:cursor-default={!hasWorkout(day)}
                  class:hover:bg-primary-700={hasWorkout(day)}
                >
                  {day}
                </button>
              </div>
            {:else}
              <div></div>
            {/if}
          {/each}
        </div>
      {/each}
    </div>

    <!-- Legend -->
    <div class="mt-3 pt-3 border-t border-gray-200 flex items-center gap-4 text-xs text-gray-600">
      <div class="flex items-center gap-2">
        <div class="w-5 h-5 rounded-full bg-primary-600"></div>
        <span>有訓練</span>
      </div>
      {#if !isCurrentMonth}
        <div class="flex items-center gap-2">
          <div class="w-5 h-5 rounded-full ring-1 ring-primary-600"></div>
          <span>今天</span>
        </div>
      {/if}
    </div>
  </div>
</div>

<!-- Workout Detail Dialog -->
{#if showDialog}
  <div
    class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
    on:click={closeDialog}
  >
    <div
      class="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-y-auto"
      on:click|stopPropagation
    >
      <!-- Dialog Header -->
      <div class="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
        <h3 class="text-xl font-semibold text-gray-800">{selectedDate} 訓練記錄</h3>
        <button
          type="button"
          on:click={closeDialog}
          class="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          title="關閉"
        >
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <!-- Dialog Content -->
      <div class="px-6 py-4 space-y-4">
        {#each selectedWorkouts as { workout, exercises }}
          <div class="border border-gray-200 rounded-lg p-4">
            <!-- Workout Header -->
            <div class="flex justify-between items-start mb-3">
              <div>
                <div class="text-lg font-medium text-gray-800">
                  {workout.type === 'strength' ? '💪 重訓' : workout.type === 'cardio' ? '🏃 有氧' : '🏋️ 訓練'}
                </div>
                <div class="text-sm text-gray-500 mt-1">
                  {formatTime(workout.date)}
                  {#if workout.duration}
                    {Math.floor(workout.duration / 60)} 分鐘
                  {/if}
                </div>
              </div>
              <div class="text-right">
                <div class="text-sm text-gray-500">總重量</div>
                <div class="text-lg font-semibold text-primary-600">
                  {calculateWorkoutVolume(exercises).toLocaleString()} kg
                </div>
              </div>
            </div>

            <!-- Exercises List -->
            {#if exercises && exercises.length > 0}
              <div class="space-y-3 mt-4">
                {#each exercises as exercise}
                  <div class="bg-gray-50 rounded p-3">
                    <div class="font-medium text-gray-800 mb-2">{exercise.exercise_name}</div>
                    {#if Array.isArray(exercise.sets) && exercise.sets.length > 0}
                      <div class="text-sm text-gray-600 space-y-1">
                        {#each exercise.sets as set, index}
                          <div class="flex items-center gap-2">
                            <span class="text-gray-400">第 {index + 1} 組：</span>
                            <span>{set.weight} kg × {set.reps} 次</span>
                            {#if set.completed}
                              <span class="text-green-600">✓</span>
                            {/if}
                          </div>
                        {/each}
                      </div>
                    {/if}
                  </div>
                {/each}
              </div>
            {:else}
              <div class="text-sm text-gray-500 italic mt-2">無動作記錄</div>
            {/if}

            <!-- Notes -->
            {#if workout.notes}
              <div class="mt-3 pt-3 border-t border-gray-200">
                <div class="text-sm text-gray-500">備註</div>
                <div class="text-sm text-gray-700 mt-1">{workout.notes}</div>
              </div>
            {/if}
          </div>
        {/each}
      </div>

      <!-- Dialog Footer -->
      <div class="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4">
        <button
          type="button"
          on:click={closeDialog}
          class="w-full btn btn-secondary"
        >
          關閉
        </button>
      </div>
    </div>
  </div>
{/if}
